/**
 * Pure helpers for scene loading: next scene index, spacers, DOM insertion.
 * No engine state — easy to read and test.
 */

import type { SceneData, SceneConfig, ChoiceCondition } from '../scenes/types'
import type { StoryEngineConfig } from './types'

/** Index of the next scene to load in linear flow (skips choice-only scenes and scenes whose `when` condition is not met). */
export function getNextLinearSceneIndex(
  scenes: SceneData[],
  fromIndex: number,
  evaluateCondition?: (condition: ChoiceCondition) => boolean,
): number | null {
  for (let i = fromIndex + 1; i < scenes.length; i++) {
    if (scenes[i].type === 'choice') continue
    if (scenes[i].when && evaluateCondition && !evaluateCondition(scenes[i].when!)) continue
    return i
  }
  return null
}

/** Build SceneConfig from engine config (only the part scenes need). */
export function buildSceneConfig(config: StoryEngineConfig): SceneConfig {
  return {
    bubble: config.bubble,
    chapterTitle: { margin: config.chapterTitle.margin },
    homework: config.homework,
    choices: config.choices,
  }
}

const SCENE_WRAPPER_CLASSES = ['scene-wrapper', 'scene', 'minigame-scene', 'chapter-title-scene', 'sticky-layout-container', 'hscroll-sticky-container']

/** True if the last child of the container looks like a scene (not a spacer) and allows a spacer after it. */
export function isLastChildAScene(container: HTMLElement): boolean {
  const last = container.lastElementChild
  if (!last) return false
  if (last.classList.contains('scene-spacer')) return false
  if (last.hasAttribute('data-no-spacer')) return false
  return SCENE_WRAPPER_CLASSES.some((c) => last.classList.contains(c))
}

/** Append one spacer div to the container. */
export function appendSpacer(container: HTMLElement): void {
  const spacer = document.createElement('div')
  spacer.className = 'scene-spacer'
  container.appendChild(spacer)
}

/** If the last child is a scene, append a spacer so the next scene has spacing. */
export function appendSpacerIfLastIsScene(container: HTMLElement): void {
  if (isLastChildAScene(container)) {
    appendSpacer(container)
  }
}

/**
 * Ensure there is a spacer after insertAfterElement; return the element to insert our scene after.
 * If the insertAfterElement has data-no-spacer, skip spacer creation and insert directly after it.
 */
export function ensureSpacerAfter(
  container: HTMLElement,
  insertAfterElement: HTMLElement
): { insertAfter: Element; afterInsertPoint: Element | null } {
  if (insertAfterElement.hasAttribute('data-no-spacer')) {
    return { insertAfter: insertAfterElement, afterInsertPoint: insertAfterElement.nextElementSibling }
  }

  const next = insertAfterElement.nextElementSibling
  const hasSpacer = next?.classList.contains('scene-spacer')

  let insertAfter: Element
  let afterInsertPoint: Element | null

  if (!hasSpacer) {
    const spacer = document.createElement('div')
    spacer.className = 'scene-spacer'
    if (next) {
      container.insertBefore(spacer, next)
    } else {
      insertAfterElement.insertAdjacentElement('afterend', spacer)
    }
    insertAfter = spacer
    afterInsertPoint = spacer.nextElementSibling
  } else {
    insertAfter = next!
    afterInsertPoint = insertAfter.nextElementSibling
  }

  return { insertAfter, afterInsertPoint }
}

/** Move sceneWrapper in DOM so it sits right after insertAfter (or before afterInsertPoint). */
export function moveWrapperAfter(
  container: HTMLElement,
  sceneWrapper: HTMLElement,
  insertAfter: Element,
  afterInsertPoint: Element | null
): void {
  if (sceneWrapper.parentElement !== container) {
    if (afterInsertPoint) {
      container.insertBefore(sceneWrapper, afterInsertPoint)
    } else {
      insertAfter.insertAdjacentElement('afterend', sceneWrapper)
    }
    return
  }

  if (afterInsertPoint && afterInsertPoint !== sceneWrapper) {
    container.insertBefore(sceneWrapper, afterInsertPoint)
  } else {
    insertAfter.insertAdjacentElement('afterend', sceneWrapper)
  }
}
